import React from 'react';
import { Link } from 'react-router-dom';
import { InputSwitch } from 'primereact/inputswitch';
import moment from 'moment';

import LabelName from 'components/UI/LabelName/LabelName';
import ImageComponent from 'shared/ImageComponent';
import constants from 'constants/index';
import { generateFullName } from 'utils/generateFullNameUtils';
import { getProfileImageFilePath, parseReminderData } from 'utils/utils';
import { downloadAttachments } from 'services/calendarServices';
import { useNavbarContext } from 'context/NavbarContext';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { reminderChannels, reminderOptions } from 'constants/dropdownOptions';

const EventDetailsView = ({ event, timezone, isClientCalendar = false }) => {
  const { downloadFile } = useNavbarContext();

  if (!event) return null;

  const { extendedProps = {} } = event;

  // Helper to safely extract event type name
  const getEventTypeName = (eventType) => {
    if (!eventType) return null;
    if (typeof eventType === 'string') return eventType;
    if (typeof eventType === 'object') {
      return eventType._event_type_ || eventType.event_type_name || eventType.event_type || eventType.label || eventType.name || null;
    }
    return null;
  };

  // Helper to get event type icon
  const getEventTypeIcon = (eventType) => {
    if (!eventType) return null;
    if (typeof eventType === 'object') {
      return eventType._event_type_icon_ || eventType.event_type_icon || eventType.icon || null;
    }
    return null;
  };

  // Helper to safely get user/contact full name
  const getPersonName = (person) => {
    if (!person) return 'Unknown';
    return generateFullName(person) || 'Unknown';
  };

  // Download attachment handler
  const onClickAttachments = async (e, fileInfo) => {
    e.preventDefault();
    const url = await downloadAttachments(fileInfo);
    downloadFile(url, fileInfo.display_name || fileInfo.file_name);
  };

  // Navigate to module helper
  const navigateToModule = (item, module) => {
    if (isClientCalendar) return '#';

    let url = '/';
    if (module === 'cases') {
      if (item.is_lead || extendedProps.is_lead) {
        url = item.case_id ? `/leads/${item.case_id}` : '/leads';
      } else {
        url = item.case_id ? `/cases/${item.case_id}` : '/cases';
      }
    } else if (module === 'users') {
      if (item?.access_level && item?.access_level !== 'client') {
        url = item?.firm_user_id ? `/settings/team-management/${item?.firm_user_id}` : '/settings/team-management';
      } else {
        url = item?.contact_id ? `/contacts/${item?.contact_id}` : '/contacts';
      }
    }
    return url;
  };

  // Get status label for attendees
  const getStatusLabel = (option) => {
    if (option) {
      if (option?.is_confirmed === false && (option?.status === 'ACTIVE' || option?.status === undefined)) {
        return ' (Unconfirmed)';
      } else if ((option?.is_confirmed || option?.is_confirmed === undefined) && option?.status === 'ARCHIVED') {
        return ' (Archived)';
      } else if (option?.is_confirmed === false && option?.status === 'ARCHIVED') {
        return ' (Archived)';
      }
    }
    return '';
  };

  const isFirmWide = Boolean(extendedProps.isFirmWide || extendedProps.firm_wide || extendedProps.is_firm_event);
  const isGoogleEvent = Boolean(extendedProps.is_google_event);
  const eventTypeIcon = isGoogleEvent ? 'fab fa-google' : getEventTypeIcon(extendedProps.eventType || extendedProps.event_type);
  const eventTypeName = isGoogleEvent ? 'Google Event' : getEventTypeName(extendedProps.eventType || extendedProps.event_type);

  return (
    <div className="event-details-view-container">
      {/* Title */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="title">
            Event Title
          </LabelName>
        </div>
        <div className="col-md-8 col-12">{event.title || 'Untitled Event'}</div>
      </div>

      {/* Case - Hidden for Google events */}
      {!isGoogleEvent && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="case">
              Case
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <div className="mb-2 casename-container text-center F-size13 m-0 p-0">
              {extendedProps.case_name ? (
                <Link className="pointer mb-0 p-2 react-tagsinput-tag text-decoration-none" to={navigateToModule(extendedProps, 'cases')}>
                  {extendedProps.case_name}
                </Link>
              ) : (
                'No case selected'
              )}
            </div>
          </div>
        </div>
      )}

      {/* Event Type */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="event_type">
            Event Type
          </LabelName>
        </div>
        <div className="col-md-8 col-12">
          {eventTypeIcon && <i className={eventTypeIcon + ' me-2'}></i>}
          {eventTypeName || 'No event type'}
        </div>
      </div>

      {/* Google Calendar Link - Only for Google events */}
      {isGoogleEvent && extendedProps.google_html_link && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="google_link">
              Google Calendar
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <a
              href={extendedProps.google_html_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary d-inline-flex align-items-center"
            >
              <i className="pi pi-external-link me-2"></i>
              Open in Google Calendar
            </a>
          </div>
        </div>
      )}

      {/* Date */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="date">
            Date
          </LabelName>
        </div>
        <div className="col-md-8 col-12">
          {event.allDay
            ? (() => {
                // For all-day events, dates are in "YYYY-MM-DD" format - parse without timezone conversion
                const startDate = moment(event.start, 'YYYY-MM-DD').format(constants.month_date_year_format);
                const endDate = moment(event.end, 'YYYY-MM-DD').format(constants.month_date_year_format);
                return startDate !== endDate ? `${startDate} - ${endDate}` : startDate;
              })()
            : convertDateFromApiToUserTimezone(event.start, timezone, constants.month_date_year_format)}
        </div>
      </div>

      {/* Time */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="time">
            Time
          </LabelName>
        </div>
        <div className="col-md-8 col-12 d-flex align-items-center">
          {event.allDay ? (
            <div>All Day</div>
          ) : (
            <>
              <div>{convertDateFromApiToUserTimezone(event.start, timezone, constants.hour_minute_12_format)}</div>
              <div className="text-bold px-4">-</div>
              <div>{convertDateFromApiToUserTimezone(event.end, timezone, constants.hour_minute_12_format)}</div>
            </>
          )}
        </div>
      </div>

      {/* Recurring Event */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="is_recurring">
            Recurring Event
          </LabelName>
        </div>
        <div className="col-md-8 col-12 d-flex align-items-center justify-content-start text-capitalize">
          <div className="w-100">
            <InputSwitch name="is_recurring" checked={extendedProps.isRecurring || false} className="input-swich" disabled={true} />
          </div>
        </div>
      </div>

      {/* Firm-Wide Event - Hidden for Google events */}
      {!isGoogleEvent && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="firm_wide">
              Firm-Wide Event
            </LabelName>
          </div>
          <div className="col-md-8 col-12 d-flex align-items-center justify-content-start text-capitalize">
            <div className="w-100">
              <InputSwitch name="firm_wide" checked={isFirmWide} className="input-swich" disabled={true} />
            </div>
          </div>
        </div>
      )}

      {/* Recurring Event Details */}
      {extendedProps.isRecurring && extendedProps.recurrence_keys && (
        <>
          {extendedProps.recurrence_keys.recurrence_limit && (
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12">
                <LabelName required={false} htmlFor="recurrence_limit">
                  Till Date
                </LabelName>
              </div>
              <div className="col-md-8 col-12">
                {moment(extendedProps.recurrence_keys.recurrence_limit?.split('T')?.[0], 'YYYY-MM-DD').format(
                  constants.month_date_year_format
                )}
              </div>
            </div>
          )}

          {extendedProps.recurrence_keys.recurrence_frequency && (
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12">
                <LabelName required={false} htmlFor="recurrence_frequency">
                  Schedule
                </LabelName>
              </div>
              <div className="col-md-8 col-12 text-capitalize">{extendedProps.recurrence_keys.recurrence_frequency}</div>
            </div>
          )}

          {extendedProps.recurrence_keys.recurrence_frequency === 'nth_weekday' && (
            <>
              {extendedProps.recurrence_keys.week_occurrence && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={false} htmlFor="week_occurrence">
                      Week Occurrence
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">{extendedProps.recurrence_keys.week_occurrence}</div>
                </div>
              )}

              {extendedProps.recurrence_keys.weekday && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={false} htmlFor="weekday">
                      Weekday
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">{extendedProps.recurrence_keys.weekday}</div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Location */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="location">
            Location
          </LabelName>
        </div>
        <div className="col-md-8 col-12">{extendedProps._office_location_ || extendedProps.location || 'None'}</div>
      </div>

      {/* Conference Room */}
      {extendedProps._room_name_ && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="conference_room">
              Conference Room
            </LabelName>
          </div>
          <div className="col-md-8 col-12">{extendedProps._room_name_ || 'Conference Room'}</div>
        </div>
      )}

      {/* Video Conference URL */}
      {extendedProps.conference_url && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="conference_url">
              Video Conference
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <a href={extendedProps.conference_url} target="_blank" rel="noopener noreferrer" className="text-primary">
              <i className="pi pi-video me-2"></i>
              Join Meeting
            </a>
          </div>
        </div>
      )}

      {/* Practice Area (for non-case events) */}
      {!extendedProps.case_name && extendedProps._practice_area_ && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="practice_area">
              Practice Area
            </LabelName>
          </div>
          <div className="col-md-8 col-12">{extendedProps._practice_area_}</div>
        </div>
      )}

      {/* Description */}
      {extendedProps.description && (
        <div className="d-flex py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="description">
              Description
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <div className="event-description" dangerouslySetInnerHTML={{ __html: extendedProps.description }} />
          </div>
        </div>
      )}

      {/* Created By */}
      <div className="d-flex py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="created_by">
            Created by
          </LabelName>
        </div>
        <div className="col-md-8 col-12">
          {isClientCalendar ? (
            <div className="align-items-center d-flex">
              {extendedProps.created_by_firm_user_id && <i className="fas fa-user-tie me-2"></i>}
              {getPersonName(extendedProps.created_by_details) || 'Unknown'}
            </div>
          ) : (
            <div className="redirection-link-no-text-transform">
              <Link
                to={
                  extendedProps.created_by_firm_user_id
                    ? `/settings/team-management/${extendedProps.created_by_firm_user_id}`
                    : '/settings/team-management'
                }
                className="text-decoration-none text-primary-dark"
              >
                {extendedProps.created_by_firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                {getPersonName(extendedProps.created_by_details) || 'Unknown'}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Attendees */}
      <div className="d-flex py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="attendees">
            Attendees
          </LabelName>
        </div>
        <div className="col-md-8 col-12">
          {extendedProps.groups && extendedProps.groups.length > 0 && (
            <div className="mb-2">
              {extendedProps.groups.map((group, index) =>
                isClientCalendar ? (
                  <div key={index} className="d-flex align-items-center mb-1">
                    <ImageComponent filePath={group?.profile_image} fileName={group?.group_name} fileSize="small.jpg" />
                    <span className="ms-2">
                      {group?.group_name || 'Group'} {group?.status === 'ARCHIVED' && '(Archived)'}
                    </span>
                  </div>
                ) : (
                  <Link
                    key={index}
                    to={`/settings/groups`}
                    className="d-flex align-items-center mb-1 text-decoration-none text-primary-dark"
                  >
                    <ImageComponent filePath={group?.profile_image} fileName={group?.group_name} fileSize="small.jpg" />
                    <span className="ms-2">
                      {group?.group_name || 'Group'} {group?.status === 'ARCHIVED' && '(Archived)'}
                    </span>
                  </Link>
                )
              )}
            </div>
          )}

          {extendedProps.guests && extendedProps.guests.length > 0 ? (
            <div>
              {extendedProps.guests.map((attendee, index) => {
                // For guest attendees (Google event guests), show email without redirection
                const isGuest = attendee?.access_level === 'guest';

                if (isClientCalendar || isGuest) {
                  return (
                    <div key={index} className="d-flex align-items-center mb-1">
                      <ImageComponent
                        filePath={
                          isGuest
                            ? null
                            : attendee?.profile_image || getProfileImageFilePath(attendee?.firm_user_id || attendee?.contact_id)
                        }
                        fileName={isGuest ? attendee?.name : getPersonName(attendee)}
                        fileSize="small.jpg"
                        style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                      />
                      <span className="ms-2">
                        {!isGuest && attendee?.firm_user_id && <i className="fas fa-user-tie me-1"></i>}
                        {isGuest ? attendee?.name : getPersonName(attendee)}
                        {!isGuest && getStatusLabel(attendee)}
                      </span>
                    </div>
                  );
                }

                return (
                  <Link
                    key={index}
                    to={
                      attendee?.firm_user_id ? `/settings/team-management/${attendee?.firm_user_id}` : `/contacts/${attendee?.contact_id}`
                    }
                    className="d-flex align-items-center mb-1 text-decoration-none text-primary-dark"
                  >
                    <ImageComponent
                      filePath={attendee?.profile_image || getProfileImageFilePath(attendee?.firm_user_id || attendee?.contact_id)}
                      fileName={getPersonName(attendee)}
                      fileSize="small.jpg"
                      style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                    />
                    <span className="ms-2">
                      {attendee?.firm_user_id && <i className="fas fa-user-tie me-1"></i>}
                      {getPersonName(attendee)}
                      {getStatusLabel(attendee)}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div>No attendees</div>
          )}
        </div>
      </div>

      {/* Attachments - Hidden for Google events */}
      {!isGoogleEvent && (
        <div className="d-flex py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="attachments">
              Attachments
            </LabelName>
          </div>
          {extendedProps.attachments && extendedProps.attachments.length > 0 ? (
            <div className="col-md-8 col-12">
              <ul className="px-0">
                {extendedProps.attachments.map((file, index) => (
                  <li className="li-style-none d-flex text-break" key={index}>
                    <i className="fas fa-paperclip me-2 mt-1"></i>
                    <a href="/" onClick={(e) => onClickAttachments(e, file)} className="text-primary">
                      {file?.file_name || file?.display_name || file?.name || `Attachment ${index + 1}`}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="col-md-8 col-12">No attachments added</div>
          )}
        </div>
      )}

      {/* Reminders */}
      {parseReminderData(extendedProps.reminder)?.length > 0 && (
        <div className="d-flex py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="reminder">
              Reminder
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {parseReminderData(extendedProps.reminder)?.map((data) => {
              return (
                <div
                  className="d-flex input-shadow align-items-center justify-content-center p-2 mb-2 bg-grey mt-2"
                  key={data?.reminder_channel_id}
                >
                  <div className="w-100">
                    <div>{reminderChannels?.find((channel) => channel?.value === data?.reminder_channel_type)?.label}</div>
                    <div>
                      {data?.value === 'custom' ? (
                        <>
                          <div>{data?.value}</div>
                          <div>{`${data?.customValue} ${data?.type} before event start time`}</div>
                        </>
                      ) : (
                        reminderOptions.find((channel) => channel.value === data?.value)?.label + ' before event start time'
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailsView;
